import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from "react-router-dom";
import cx from 'classnames';
import _ from 'lodash';

import { fetchingAssetInteraction, fetchingLeadInteractionChartForAsset } from 'actions/leads';
import { EmptyMessage, LoadingMessage, TableInfo, AssetInteractionsHeader } from './index';
import { TrendBarChart, TrendsAreaChart, TrendsPieChart } from '../../reports/components/charts'
import { TableFooter } from '../../reports/components/tableFooter';
import { DropdownFilter } from '../../reports/components/dropDown'
import { IKImage } from 'imagekitio-react';
import styles from '../leadReports.module.scss';
import Truncate from 'react-truncate';
import { getWidthForAnalyticsBar } from '../../reports/components';
import FileService from 'services/file.service';
import { fetchAnalyticsChartForAssetAnalytics } from 'actions/storyboardReports';
import { formatDurationWithLabel, safeDivide } from '../../reports/reporthelpers';

const defaultContentImage = `/cleverstory/images/default-content.png`;

export const AssetInteractions = (props) => {
  const dispatch = useDispatch();

  const { profileId, reportDate } = props;
  let { storyboardId } = useParams();
  const assets = useSelector(state => _.get(state, 'lead.assetInteractions.items')) || [];
  const chartData = useSelector(state => _.get(state, 'lead.assetInteractions.chartItems'));
  const pagination = useSelector(state => _.get(state, 'lead.assetInteractions.pagination'));
  const loading = useSelector(state => _.get(state, 'lead.assetInteractions.loading'));
  const [currentChart, setCurrentChart] = useState("table_view");
  const [viewOption, setViewOption] = useState({ label: 'Views', value: 'views', name: 'name' })

  useEffect(() => {
    if (reportDate) {
      handlePageChange(0);
    }
  }, [reportDate]);

  const handlePageChange = (page) => {
    handleFetchAssetAnalyticsReport(page);
  }

  const handleFetchAssetAnalyticsReport = (page) => {
    var request = {
      storyboard: storyboardId,
      ...reportDate,
    }

    dispatch(fetchingAssetInteraction(profileId, request, page, 4));
    dispatch(fetchingLeadInteractionChartForAsset(profileId, request, 0, 6));
  }

  const handleArrange = (data) => {
    setViewOption(state => {
      return {
        ...state,
        label: data.label,
        value: data.value
      }
    });
  }

  const handleChartChange = (value) => {
    setCurrentChart(value)
  }

  return (
    <div className={styles.assetAnalyticsWrapper}>
      <div className={styles.assetAnalyticsContainer}>
        <TableInfo
          name="Asset Interactions"
          pagination={pagination}
        />

        {currentChart == "table_view" &&
          <AssetInteractionsHeader />
        }

        {currentChart == "pie_chart_view" && <DropdownFilter disableOption={['visitors']} arrangeOption={viewOption} handleArrange={handleArrange} />}

        {currentChart == "table_view" &&
          !loading && assets && assets.map((data, index) => {
            return (
              <AssetsReportView
                key={index}
                data={data}
                loading={loading}
                storyboard={storyboardId}
                index={index}
                date={props.date}
              />
            )
          })
        }

        {(_.size(chartData) > 0 && currentChart == "pie_chart_view") &&
          <div className={styles.graphSmallView}>
            <TrendsPieChart data={chartData} viewOption={viewOption} height={320} width="100%" />
          </div>
        }

        {loading && (assets && assets.length > 0) &&
          <LoadingMessage />
        }

        {(assets && assets.length == 0) && !loading &&
          <EmptyMessage />
        }
      </div>

      <TableFooter
        currentChart={currentChart}
        disableView={["graph_view"]}
        handleChartChange={handleChartChange}
        handlePageChange={handlePageChange}
        pagination={pagination}
        leftAlign={true}
        paginationName={"Assets"}
        profileId={profileId}
      />
    </div>
  )
}

const AssetsReportView = ({ loading, data, profileId }) => {
  const dispatch = useDispatch();
  const assetMappingId = _.get(data, 'asset.content.id', null);
  const assetsAnalyticsData = useSelector(state => _.get(state, `storyboards.storyboard.reports.analytics.asset.items[${assetMappingId}]`));

  useEffect(() => {
    const assetData = _.get(data, 'asset.content.asset', null);

    if (assetMappingId){
      var request = {
        "assetMappingId": assetMappingId,
        "contact": profileId,
      }
      
      dispatch(fetchAnalyticsChartForAssetAnalytics(request, assetData));
    }
  }, [data.asset]);

  if (!data || data.length <= 1) {
    return false;
  }

  const assetData = _.get(data, 'asset');
  const name = getFieldSelector(assetData, "name");
  const assetType = getFieldSelector(assetData, "contentType");
  const assetSize = getFieldSelector(assetData, "fileSize");

  const icon = getFieldSelector(assetData, "icon");
  const iconUrl = _.get(icon, 'url') || defaultContentImage;

  const renderTrends = (analytics, assetData) => {
    const metaData = _.get(assetData, 'asset.content.asset.metadata', null);
    let contentType = FileService.fileContentType(metaData);
    
    switch (contentType) {
      case "DOCUMENT":
          return <TrendBarChart data={analytics} dataKey="totalTime" width={getWidthForAnalyticsBar(analytics, true)} />
      
      case "AUDIO": 
      case "VIDEO":
        contentType = contentType.toLowerCase();
        const totalDuration = _.get(metaData, `${contentType}_duration`, 0);
        const segmentLength = _.get(metaData, `${contentType}_segment_length`, 1);
        const totalSegments = Math.round(totalDuration) / segmentLength;

        const generateAnalytics = (analytics, totalSegments, segmentLength) => {
          let time = 0;

          return Array.from({ length: totalSegments }, (_, index) => {
            const existingItem = analytics.find(item => item.index === index);
            time = time + segmentLength;
            return existingItem ? { ...existingItem, currentTime: time} : { index: index + 1, currentTime: time, totalTime: 0, count: 0 };
          })
        }

        const updatedAnaltyics = generateAnalytics( analytics, totalSegments, segmentLength);

        if(!totalSegments){
          return <span className={styles.hypen}> - </span>
        }
        
        return <TrendsAreaChart data={updatedAnaltyics} height={50} width={getWidthForAnalyticsBar(updatedAnaltyics, true)} />
      default:
          return '-';
      }
  }

  return (
    <div className={styles.assetAnalyticsRow}>
      <div className={cx(styles.wrapper, styles.assetColumn)}>
        <div className={styles.assetIconContainer}>
          <IKImage
            className={styles.assetIcon}
            path={iconUrl}
            transformation={[{
              "width": "600"
            }]}
          />
        </div>
        <div className={styles.assetDetails}>
          <div className={styles.assetName}>
            <Truncate lines={1}>
              <span>{name}</span>
            </Truncate>
          </div>
          <div className={styles.assetFormat}>
            <span className={styles.type}>{assetType}</span>
            <span className={styles.size}>{assetSize}</span>
          </div>
        </div>
      </div>

      <div className={cx(styles.wrapper, styles.viewsColumn)}>
        <div className={styles.headline}>{data.views}</div>
      </div>

      <div className={cx(styles.wrapper, styles.avgTimeSpentColumn)}>
        <div className={styles.headline}>{formatDurationWithLabel(data.timeSpent).value}</div>
      </div>

      <div className={cx(styles.wrapper, styles.bounceColumn)}>
        <div className={styles.headline}>{Math.round(data.percentageCompleted)}%</div>
      </div>

      <div className={cx(styles.wrapper, styles.downloadColumn)}>
        <div className={styles.headline}>{data.downloads}</div>
      </div>
      <div className={cx(styles.wrapper, styles.trendsColumn)}>
        {(assetsAnalyticsData && assetsAnalyticsData.length > 0) ?
          renderTrends(assetsAnalyticsData, data) : <span className={styles.hypen}> - </span>
        }
      </div>
    </div>
  )
}

const getFieldSelector = (mapping, key) => {
  const fields = _.get(mapping, 'fields') || [];
  const field = fields.find(item => item.name === key);

  return _.get(field, 'content', "");
}
